import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toSnakeCaseKeys, toItemArray } from '@shared/api/keys';
import type { ClientId } from '../../domain/ids';
import type { Client, ClientWritePayload, DcAssignmentRow } from '../../domain/client/client.types';
import type { DcRole, AssignmentStatus } from '../../domain/shared.types';

// Migrado a v1 (T45). Decisiones de contrato (specs/api/core-manage-api-v1.json):
// - `GET /clients` pagina (`PaginatedClientsDto {items,page,pageSize,total}`); `GET/PATCH /clients/
//   {id}` responden `ClientDetailDto` directo (camelCase, ya desenvuelto de `ApiResponse` por el
//   interceptor). Se sigue el mismo patrón que el resto de la migración: DTO zod en snake_case,
//   conversión de casing en el borde vía `toSnakeCaseKeys`.
// - `update()`: v1 solo expone PATCH (no PUT) y `ClientDetailDto.version` es un campo requerido de
//   la respuesta → concurrencia optimista (mismo patrón que `datacenter.repository.ts` T31): se
//   relee el cliente antes de aplicar el PATCH para adjuntar `version`.
// - `deliveryUnit`/`practiceUnit` en la respuesta son objetos `{id,name,code}` — `code` es el
//   equivalente real de `duCode`/`puCode` (la inconsistencia "campo _id que en realidad guarda un
//   CODE" ya existía en el legado, spec.md §2). El *write* (`deliveryUnitId`/`practiceUnitId`) no
//   tiene schema documentado (`JsonObject`) y la UI solo conoce el CODE (`ClientFormPage.tsx`
//   value={d.code}) — se sigue enviando el código bajo esa clave, sin resolver a un id real
//   (gap **G-26**, mismo espíritu que G-19 de datacenters).
// - `DcRole`/`kind` de asignación de datacenter: v1 usa enums en inglés
//   (`DatacenterAssignmentRoleDto`: primary/disaster_recovery/associated;
//   `UnifiedFacilityKindDto`: kyndryl/client_owned) distintos de los valores legado en español que
//   el dominio conserva (`Principal`/`Recuperación`/`Asociado`, `kyndryl`/`client`) — se traduce en
//   el borde del repositorio, con passthrough para valores no mapeados (mismo patrón T38
//   `vmCluster.repository.ts`).
// - `getDatacenters` (lista liviana por-cliente) → `GET /clients/{id}/datacenter-assignments`
//   (renombrado desde `/datacenters`); `updateDatacenterAssignments` (bulk) → `PUT` al mismo path
//   (antes `PUT /clients/{id}/datacenters`); create/update/remove de una asignación individual usan
//   `/clients/{id}/datacenter-assignments[/{datacenterId}]` (create sin cambios, update ahora PATCH
//   en vez de PUT — el path param que v1 llama `datacenterId` es la misma entidad que el legado
//   llamaba "assignmentId": 1 asignación por datacenter-cliente).
// - `getAllDatacenters` (fila unificada Kyndryl/propio) → `GET /clients/{id}/datacenters/unified`
//   (antes `/all-datacenters`). Campos perdidos sin equivalente en `UnifiedClientDatacenterDto`:
//   `countryId`/`countryName`/`city`/`address`/`notes` (gap **G-26**, consumido por
//   `DcAssignmentTable.tsx:184` — se degradan a `undefined`, la UI ya maneja el caso con `'—'`).
// - `getUsers` (UC-5, rol + % de asignación): `GET /clients/{id}/users` documentado en v1 SOLO trae
//   `{id,displayName,email,userType,status}` — sin rol ni porcentaje, no sirve. Se usa en su lugar
//   `GET /clients/{id}/assignment-context` (sin schema documentado, `JsonOk` genérico) — gap
//   **G-24**. Forma real confirmada contra backend (2026-08-21):
//   `{userId, displayName, email, status, assignmentState, remainingDedicationPercentage}`, que
//   tampoco trae rol ni el % de este cliente. `firstName`/`lastName` se parten de `displayName`,
//   `username` sale de `email`, `roleName` queda `''` y `totalAssignedPercentage` se deriva como
//   `100 - remainingDedicationPercentage`.
// - `getUsersByContext` (`/users/by-context/:id`) sigue existiendo con la misma forma de envelope
//   `{otherUsers,ownUsers}`, pero su schema documentado tampoco trae `roleName`/porcentajes (mismo
//   hueco que G-24) — sin consumidor real hoy, se deja el parseo tolerante (campos opcionales).
const OrgUnitRefDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
});

const ClientDtoSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  nit: z.string().optional(),
  address: z.string().nullish(),
  country_id: z.string().nullish(),
  country_name: z.string().nullish(),
  enterprise_id: z.string().nullish(),
  enterprise_name: z.string().nullish(),
  delivery_unit: OrgUnitRefDtoSchema.nullish(),
  practice_unit: OrgUnitRefDtoSchema.nullish(),
  is_group: z.boolean().optional(),
  version: z.number().optional(),
});

function toDomain(dto: z.infer<typeof ClientDtoSchema>): Client {
  return {
    id: dto.id as ClientId,
    name: dto.name,
    nit: dto.nit,
    address: dto.address ?? undefined,
    countryId: dto.country_id ?? undefined,
    countryName: dto.country_name ?? undefined,
    enterpriseId: dto.enterprise_id ?? undefined,
    enterpriseName: dto.enterprise_name ?? undefined,
    puCode: dto.practice_unit?.code,
    duCode: dto.delivery_unit?.code,
    isGroup: dto.is_group,
  };
}

// Traducción de enums v1 (inglés) ↔ dominio (español, legado) — passthrough para valores no
// mapeados (mismo patrón que `vmCluster.repository.ts`, T38).
const ROLE_TO_DOMAIN: Record<string, DcRole> = {
  primary: 'Principal',
  disaster_recovery: 'Recuperación',
  associated: 'Asociado',
};
const ROLE_TO_WIRE: Record<string, string> = {
  Principal: 'primary',
  Recuperación: 'disaster_recovery',
  Asociado: 'associated',
};
function toDomainRole(role: string | null | undefined): DcRole | undefined {
  if (!role) return undefined;
  return ROLE_TO_DOMAIN[role] ?? (role as DcRole);
}
function toWireRole(role: DcRole): string {
  return ROLE_TO_WIRE[role] ?? role;
}

const KIND_TO_DOMAIN: Record<string, 'kyndryl' | 'client'> = {
  kyndryl: 'kyndryl',
  client_owned: 'client',
};

// GET /clients/:clientId/datacenters/unified (ClientAllDatacentersTab.jsx:150-166, UC-4) — fila
// unificada Kyndryl/propio. `countryId`/`countryName`/`city`/`address`/`notes` sin equivalente en
// `UnifiedClientDatacenterDto` (G-26, ver comentario de cabecera) — no se parsean, quedan undefined.
const DcAssignmentRowDtoSchema = z.object({
  kind: z.string(),
  id: z.string(),
  name: z.string(),
  role: z.string().nullish(),
  status: z.string(),
  room_count: z.number(),
  vm_count: z.number(),
  linked_companies: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      })
    )
    .optional(),
});

function toDcAssignmentRow(dto: z.infer<typeof DcAssignmentRowDtoSchema>): DcAssignmentRow {
  return {
    kind: KIND_TO_DOMAIN[dto.kind] ?? (dto.kind as 'kyndryl' | 'client'),
    id: dto.id,
    name: dto.name,
    role: toDomainRole(dto.role),
    assignmentStatus: dto.status as AssignmentStatus | undefined,
    roomsCount: dto.room_count,
    vmsCount: dto.vm_count,
    linkedCompanies: dto.linked_companies?.map((lc) => ({
      companyId: lc.id,
      companyName: lc.name,
    })),
  };
}

// GET /clients/:id/datacenter-assignments (ClientDetailPage.jsx:281, 303-330) — lista liviana de DCs
// Kyndryl asignados a este cliente para el editor legacy UC-4b, sin los conteos/empresas de
// /datacenters/unified. No hay tipo de domain/ para esta forma (más estrecha que DcAssignmentRow):
// se modela aquí, en infrastructure/, en vez de forzar el shape completo de DcAssignmentRow (que
// exige roomsCount/vmsCount que este endpoint no devuelve).
export interface ClientDatacenterAssignment {
  id: string;
  name: string;
  role?: DcRole;
  assignmentStatus?: AssignmentStatus;
}

const ClientDatacenterAssignmentDtoSchema = z.object({
  datacenter_id: z.string(),
  name: z.string(),
  role: z.string().nullish(),
  status: z.string().optional(),
});

function toDatacenterAssignment(
  dto: z.infer<typeof ClientDatacenterAssignmentDtoSchema>
): ClientDatacenterAssignment {
  return {
    id: dto.datacenter_id,
    name: dto.name,
    role: toDomainRole(dto.role),
    assignmentStatus: dto.status as AssignmentStatus | undefined,
  };
}

export interface ClientDatacenterAssignmentInput {
  datacenterId: string;
  role: DcRole;
  status: AssignmentStatus;
}

// GET /clients/:id/assignment-context (G-24) y GET /users/by-context/:id (:397-399, envuelto en
// { otherUsers: [...] }) comparten la misma forma de fila de usuario asumida (spec.md §2
// ClientUser). El tipo de domain/ correspondiente (`ClientUser`, T14) no existe todavía en este
// punto de la secuencia de tareas — se modela aquí, en infrastructure/, sin inventar un nombre de
// domain/.
export interface ClientUserSummary {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  roleName: string;
  assignedPercentage?: number;
  totalAssignedPercentage?: number;
}

// Forma REAL confirmada contra backend v1 (G-24): `{userId, displayName, email, status,
// assignmentState, remainingDedicationPercentage}` — no la del legado que se asumió al migrar.
const ClientUserDtoSchema = z.object({
  user_id: z.union([z.string(), z.number()]).transform(String),
  display_name: z.string().nullish(),
  email: z.string().nullish(),
  remaining_dedication_percentage: z.coerce.number().optional(),
});

// `displayName` es el único nombre que da v1: se parte por el primer espacio para alimentar los
// dos campos que la UI muestra por separado (diálogo de edición/borrado).
function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
  const trimmed = displayName.trim();
  const separator = trimmed.indexOf(' ');
  if (separator === -1) return { firstName: trimmed, lastName: '' };
  return {
    firstName: trimmed.slice(0, separator),
    lastName: trimmed.slice(separator + 1),
  };
}

function toClientUser(dto: z.infer<typeof ClientUserDtoSchema>): ClientUserSummary {
  const { firstName, lastName } = splitDisplayName(dto.display_name ?? '');
  const remaining = dto.remaining_dedication_percentage;
  return {
    id: dto.user_id,
    firstName,
    lastName,
    username: dto.email ?? '',
    roleName: '',
    // v1 no expone el % asignado A ESTE cliente, solo la dedicación restante global del usuario
    // (G-24): `assignedPercentage` queda sin fuente y `totalAssignedPercentage` se deriva de ella.
    assignedPercentage: undefined,
    totalAssignedPercentage: remaining === undefined ? undefined : 100 - remaining,
  };
}


export const clientRepository = {
  async getAll(): Promise<Client[]> {
    const { data } = await apiClient.get('/clients');
    return z
      .array(ClientDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDomain);
  },

  async getById(id: ClientId): Promise<Client | null> {
    const { data } = await apiClient.get(`/clients/${id}`);
    if (!data) return null;
    return toDomain(ClientDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // countryId/enterpriseId se reenvían como reciben (ids reales); deliveryUnitId/practiceUnitId
  // reciben el CODE de duCode/puCode (G-26, ver comentario de cabecera).
  async create(payload: ClientWritePayload): Promise<Client> {
    const { data } = await apiClient.post('/clients', {
      name: payload.name,
      nit: payload.nit,
      address: payload.address,
      countryId: payload.country_id,
      enterpriseId: payload.enterprise_id,
      deliveryUnitId: payload.du_id,
      practiceUnitId: payload.pod_id,
    });
    return toDomain(ClientDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // v1 exige la `version` leída para actualizar (concurrencia optimista, mismo patrón que
  // `datacenter.repository.ts` T31) y solo expone PATCH (no PUT).
  async update(
    id: ClientId,
    payload: Partial<ClientWritePayload> & { is_group?: boolean }
  ): Promise<Client> {
    const { data: current } = await apiClient.get(`/clients/${id}`);
    const { version } = ClientDtoSchema.parse(toSnakeCaseKeys(current));
    const body: Record<string, unknown> = { version };
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.nit !== undefined) body.nit = payload.nit;
    if (payload.address !== undefined) body.address = payload.address;
    if (payload.country_id !== undefined) body.countryId = payload.country_id;
    if (payload.enterprise_id !== undefined) body.enterpriseId = payload.enterprise_id;
    if (payload.du_id !== undefined) body.deliveryUnitId = payload.du_id;
    if (payload.pod_id !== undefined) body.practiceUnitId = payload.pod_id;
    if (payload.is_group !== undefined) body.isGroup = payload.is_group;
    const { data } = await apiClient.patch(`/clients/${id}`, body);
    return toDomain(ClientDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend tal cual (patrón rack.repository.remove) — el mapeo a mensaje
  // legible ocurre en application/, no aquí.
  async remove(id: ClientId): Promise<void> {
    await apiClient.delete(`/clients/${id}`);
  },

  async getDatacenters(id: ClientId): Promise<ClientDatacenterAssignment[]> {
    const { data } = await apiClient.get(`/clients/${id}/datacenter-assignments`);
    return z
      .array(ClientDatacenterAssignmentDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDatacenterAssignment);
  },

  // PUT /clients/:id/datacenter-assignments { assignments: [{datacenterId, role, status}] } —
  // ClientDetailPage.jsx:352-385 / ClientAllDatacentersTab.jsx:429-441 (bulk edit legacy, UC-4b).
  async updateDatacenterAssignments(
    id: ClientId,
    assignments: ClientDatacenterAssignmentInput[]
  ): Promise<void> {
    await apiClient.put(`/clients/${id}/datacenter-assignments`, {
      assignments: assignments.map((a) => ({
        datacenterId: a.datacenterId,
        role: toWireRole(a.role),
        status: a.status,
      })),
    });
  },

  async getUsers(id: ClientId): Promise<ClientUserSummary[]> {
    const { data } = await apiClient.get(`/clients/${id}/assignment-context`);
    return z
      .array(ClientUserDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toClientUser);
  },

  async getAllDatacenters(clientId: ClientId): Promise<DcAssignmentRow[]> {
    const { data } = await apiClient.get(`/clients/${clientId}/datacenters/unified`);
    return z
      .array(DcAssignmentRowDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDcAssignmentRow);
  },

  // POST /clients/:clientId/datacenter-assignments {datacenterId, role} —
  // ClientAllDatacentersTab.jsx:374-390 (asignar DC Kyndryl, UC-4).
  async createDatacenterAssignment(
    clientId: ClientId,
    data: { datacenterId: string; role: DcRole }
  ): Promise<void> {
    await apiClient.post(`/clients/${clientId}/datacenter-assignments`, {
      datacenterId: data.datacenterId,
      role: toWireRole(data.role),
    });
  },

  // PATCH /clients/:clientId/datacenter-assignments/:datacenterId {role} — v1 solo expone PATCH (no
  // PUT). ClientAllDatacentersTab.jsx:451-470 (cambiar rol de un DC kyndryl por chip, UC-4).
  async updateDatacenterAssignment(
    clientId: ClientId,
    assignmentId: string,
    data: { role: DcRole }
  ): Promise<void> {
    await apiClient.patch(`/clients/${clientId}/datacenter-assignments/${assignmentId}`, {
      role: toWireRole(data.role),
    });
  },

  // DELETE /clients/:clientId/datacenter-assignments/:id — ClientAllDatacentersTab.jsx:473-487
  // (desasignar kyndryl; el DC no se borra, UC-4).
  async removeDatacenterAssignment(clientId: ClientId, assignmentId: string): Promise<void> {
    await apiClient.delete(`/clients/${clientId}/datacenter-assignments/${assignmentId}`);
  },

  // GET /users/by-context/:id (ClientDetailPage.jsx:391-404, UC-5) — envuelto en
  // { otherUsers: [...] }, no un array crudo. Sin roleName/porcentajes en el schema documentado
  // (mismo hueco que G-24) — sin consumidor real hoy.
  async getUsersByContext(id: ClientId): Promise<ClientUserSummary[]> {
    const { data } = await apiClient.get(`/users/by-context/${id}`);
    const parsed = z
      .object({ other_users: z.array(ClientUserDtoSchema).optional() })
      .parse(toSnakeCaseKeys(data));
    return (parsed.other_users ?? []).map(toClientUser);
  },
};
